import { useEffect, useState } from "react";
import { Card } from "@/components/Kpi";
import { api } from "@/lib/api";
import { SelettoreAgenzie } from "@/components/corto/SelettoreAgenzie";
import { COLORS, ComparisonChart } from "@/components/Charts";
import { dateIt, eur, num } from "@/lib/format";

const gg = (v) => dateIt(v);

/** Liquidato progressivo (YTD) dell'anno in corso confrontato con lo stesso periodo dell'anno prima. */
const PRODOTTI_YTD = [["", "Tutti i prodotti"], ["garantito", "Garantito"], ["non_garantito", "Non garantito"]];

export const ConfrontoYtd = ({ ytd, titolo = "Liquidato progressivo (YTD)", testId, periodo,
  onPeriodo, agenzie, escluse: escluseProp, onEscluse, prodotto, onProdotto }) => {
  if (!ytd?.serie?.length) return null;
  const confronto = ytd;
  const dati = ytd.serie;
  const totPrec = ytd.totale_precedente;
  const totCorr = ytd.totale_corrente;
  const scostamento = ytd.scostamento_eur;
  const perc = ytd.scostamento_perc;
  const positivo = scostamento >= 0;
  const tinta = positivo ? "#15803D" : "#DC2626";

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-4" data-testid={testId}>
      <div className="xl:col-span-3">
        <ComparisonChart height={340} testId={`${testId}-chart`}
          title={`${titolo}${ytd.prodotto ? ` · ${ytd.prodotto === "garantito" ? "Garantito" : "Non garantito"}` : ""} · ${confronto.anno_precedente} vs ${confronto.anno} · dal ${gg(ytd.periodo.dal)} al ${gg(ytd.periodo.al)}`}
          data={dati}
          keys={[{ key: "precedente", name: `Progressivo ${confronto.anno_precedente}`, color: COLORS.grigio },
            { key: "corrente", name: `Progressivo ${confronto.anno}`, color: COLORS.liquidato }]} />
      </div>
      <Card className="p-5 flex flex-col justify-center gap-4" data-testid={`${testId}-box`}>
        {onEscluse && agenzie?.length ? (
          <div className="pb-3 border-b border-slate-100">
            <SelettoreAgenzie agenzie={agenzie} escluse={escluseProp || []}
              etichetta="Agenzie incluse" testId={`${testId}-escludi`} onChange={onEscluse} />
          </div>
        ) : null}
        {onProdotto ? (
          <div className="pb-3 border-b border-slate-100">
            <span className="block uppercase text-[10px] tracking-[0.14em] font-semibold text-slate-400 mb-1">Prodotto</span>
            <div className="grid grid-cols-3 gap-1" data-testid={`${testId}-prodotto`}>
              {PRODOTTI_YTD.map(([k, l]) => (
                <button key={k} type="button" onClick={() => onProdotto(k)} data-testid={`${testId}-prodotto-${k || "tutti"}`}
                  className={`text-[11px] px-2 py-1 rounded-md border transition-colors duration-200 ${
                    (prodotto || "") === k ? "bg-[#0018A8] text-white border-[#0018A8]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#0018A8]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {onPeriodo ? (
          <div className="grid grid-cols-2 gap-2 pb-3 border-b border-slate-100">
            {[["dal", "Dal"], ["al", "Al"]].map(([campo, label]) => (
              <label key={campo} className="block">
                <span className="block uppercase text-[10px] tracking-[0.14em] font-semibold
                  text-slate-400 mb-1">{label}</span>
                <input type="date" value={(periodo || {})[campo] || ""}
                  data-testid={`${testId}-${campo}`}
                  onChange={(e) => onPeriodo({ ...(periodo || {}), [campo]: e.target.value })}
                  className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs font-mono
                    focus:outline-none focus:ring-2 focus:ring-[#0018A8]/30" />
              </label>
            ))}
            <button type="button" data-testid={`${testId}-reset`}
              onClick={() => onPeriodo({})}
              className="col-span-2 text-[11px] uppercase tracking-[0.12em] font-semibold
                text-slate-400 hover:text-[#C8102E] transition-colors duration-200">
              Azzera periodo
            </button>
          </div>
        ) : null}
        <div>
          <div className="uppercase text-[10px] tracking-[0.16em] font-semibold text-slate-400">
            {gg(ytd.periodo.dal_precedente)} → {gg(ytd.periodo.al_precedente)}
          </div>
          <div className="font-mono text-lg font-semibold text-slate-600"
            data-testid={`${testId}-prec`}>{eur(totPrec)}</div>
        </div>
        <div>
          <div className="uppercase text-[10px] tracking-[0.16em] font-semibold text-slate-400">
            {gg(ytd.periodo.dal)} → {gg(ytd.periodo.al)}
          </div>
          <div className="font-mono text-xl font-bold text-slate-900"
            data-testid={`${testId}-corr`}>{eur(totCorr)}</div>
        </div>
        <div className="pt-3 border-t border-slate-100">
          <div className="uppercase text-[10px] tracking-[0.16em] font-semibold text-slate-400">
            Scostamento
          </div>
          <div className="font-mono text-xl font-bold" style={{ color: tinta }}
            data-testid={`${testId}-scostamento-eur`}>
            {positivo ? "+" : ""}{eur(scostamento)}
          </div>
          <div className="font-mono text-sm font-semibold" style={{ color: tinta }}
            data-testid={`${testId}-scostamento-perc`}>
            {perc === null ? "—" : `${positivo ? "+" : ""}${num(perc, 2)}%`}
          </div>
        </div>
      </Card>
    </div>
  );
};

/** Blocco YTD autonomo: periodo ed esclusioni indipendenti dai filtri di pagina. */
export const ConfrontoYtdAutonomo = ({ agenzie, slug, titolo, testId, onStato, extra }) => {
  const [ytd, setYtd] = useState(null);
  const [periodo, setPeriodo] = useState({});
  const [escluse, setEscluse] = useState([]);
  const [prodotto, setProdotto] = useState("");

  useEffect(() => {
    const params = { ...periodo };
    Object.entries(extra || {}).forEach(([k, v]) => { if (v) params[k] = v; });
    if (slug) params.slug = slug;
    if (escluse.length) params.escludi = escluse.join(",");
    if (prodotto) params.prodotto = prodotto;
    api.get("/corto/ytd", { params }).then((r) => setYtd(r.data)).catch(() => setYtd(null));
    if (onStato) {
      onStato({ ytd_dal: periodo.dal || "", ytd_al: periodo.al || "",
        ytd_escludi: escluse.join(","), ytd_prodotto: prodotto });
    }
  }, [periodo, escluse, slug, prodotto, JSON.stringify(extra || {})]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ConfrontoYtd ytd={ytd} titolo={titolo} testId={testId} periodo={periodo} onPeriodo={setPeriodo}
      agenzie={slug ? null : agenzie} escluse={escluse} onEscluse={setEscluse}
      prodotto={prodotto} onProdotto={setProdotto} />
  );
};
